import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import {
  IoMdAdd,
  IoMdCheckmark,
  IoMdClose,
  IoMdRemove,
  IoMdTrash,
} from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCartItem,
  fetchCartItems,
  updateCart,
} from "../redux/features/cartSlice";
import { getVariation } from "../redux/features/VariationSlice";

const CartCard = ({ item }) => {
  const dispatch = useDispatch();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedLitre, setSelectedLitre] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const variations = useSelector((state) => state.variation.variations);
  const user_id = useSelector((state) => state.login.me?.id);

  const handleOpenDialog = (id) => {
    setSelectedItemId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItemId(null);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await dispatch(deleteCartItem(id));
      setOpenDialog(false);
    } catch (err) {
      setError("Failed to delete item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Initialize selected variation fields if variation exists
  useEffect(() => {
    if (item?.variation) {
      dispatch(getVariation(item?.variation?.id));
      setSelectedSize(item.variation.size);
      setSelectedColor(item.variation.color);
      setSelectedBrand(item.variation.brand);
      setSelectedLitre(item.variation.litre);
    }
  }, [item?.variation, dispatch]);

  // Handle size selection
  const handleSizeClick = (size) => {
    setSelectedSize((prevSize) => (prevSize === size ? null : size));
  };

  // Handle color selection
  const handleColorClick = (color) => {
    setSelectedColor((prevColor) => (prevColor === color ? null : color));
  };

  // Handle brand selection
  const handleBrandClick = (brand) => {
    setSelectedBrand((prevBrand) => (prevBrand === brand ? null : brand));
  };

  // Handle litre selection
  const handleLitreClick = (litre) => {
    setSelectedLitre((prevLitre) => (prevLitre === litre ? null : litre));
  };

  // Find the selected variation based on selected options
  useEffect(() => {
    if (item?.variation && item?.variation?.product?.variations?.length > 0) {
      const variation = item.variation.product.variations.find(
        (v) =>
          v.size === selectedSize &&
          v.color === selectedColor &&
          v.brand === selectedBrand &&
          v.litre === selectedLitre
      );
      setSelectedVariation(variation);
    }
  }, [
    item?.variation,
    selectedSize,
    selectedColor,
    selectedBrand,
    selectedLitre,
  ]);

  // Update cart with the new variation if it changes
  useEffect(() => {
    const updateCartVariation = async () => {
      if (
        selectedVariation?.id &&
        selectedVariation?.id !== item?.variation?.id
      ) {
        try {
          setLoading(true);
          await dispatch(
            updateCart({
              id: item?.id,
              data: {
                variation: selectedVariation?.id,
              },
            })
          );
          await dispatch(fetchCartItems({ user_id }));
        } catch (err) {
          setError("Failed to update cart. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    };
    updateCartVariation();
  }, [selectedVariation?.id, item?.id, item?.variation?.id, dispatch, user_id]);

  // Determine the data to display (variation or product)
  const displayData = item?.variation ? item.variation?.product : item.product;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="relative flex flex-col items-center gap-6 p-6 overflow-hidden bg-white border border-gray-100 md:flex-row rounded-xl"
    >
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 z-10 flex items-center justify-center bg-white/80"
        >
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 border-4 rounded-full border-t-red-600 border-b-red-300 border-l-red-300 border-r-red-300 animate-spin"></div>
            <p className="mt-2 font-medium text-red-600">Updating...</p>
          </div>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="absolute top-0 left-0 right-0 flex items-center gap-2 p-3 text-red-500 bg-red-50"
        >
          <FiAlertTriangle className="text-lg" />
          <p className="text-sm font-medium">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <IoMdClose />
          </button>
        </motion.div>
      )}

      {/* Product Image with hover effect */}
      <motion.div
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="flex items-center justify-center w-full p-2 overflow-hidden rounded-lg md:w-1/4 aspect-square bg-gray-50"
      >
        <img
          src={
            variations?.product?.gallery?.[0]?.url ||
            item?.product?.gallery?.[0]?.url ||
            "/placeholder.svg"
          }
          alt={displayData?.name}
          className="object-contain w-full h-full"
        />
      </motion.div>

      {/* Product Details */}
      <div className="flex-1 w-full">
        <div className="flex items-start justify-between">
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xl font-bold text-gray-800 capitalize"
          >
            {displayData?.name}
          </motion.h3>

          {/* Delete Button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            disabled={loading}
            className="text-gray-400 transition-colors hover:text-red-500"
            onClick={() => handleOpenDialog(item?.id)}
          >
            <IoMdClose className="text-2xl" />
          </motion.button>
        </div>

        {/* Variation Details (if variation exists) */}
        {item?.variation ? (
          <div className="mt-4 space-y-5">
            {/* Price with animation */}
            {selectedVariation ? (
              <>
                {selectedVariation?.stock ? (
                  <motion.div layout className="flex items-center gap-3">
                    {selectedVariation?.price !==
                    selectedVariation?.selling_price ? (
                      <motion.del
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 0.6, x: 0 }}
                        className="text-lg font-medium text-gray-400"
                      >
                        Rs. {selectedVariation?.price}
                      </motion.del>
                    ) : null}
                    <motion.p
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-red-600"
                    >
                      Rs. {selectedVariation?.selling_price}
                    </motion.p>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 border border-red-100 rounded-lg bg-red-50"
                  >
                    <h1 className="flex items-center gap-2 font-bold text-red-500 uppercase">
                      <FiAlertTriangle />
                      Out of Stock{" "}
                      <span className="text-sm font-normal text-red-400 lowercase text-wrap">
                        (remove from cart or select other variant)
                      </span>
                    </h1>
                  </motion.div>
                )}
              </>
            ) : (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="inline-block p-2 text-lg font-semibold text-red-500 rounded-md bg-red-50"
              >
                No matched variation
              </motion.p>
            )}

            {/* Variation Options */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Size Selection */}
              {item?.variation?.product?.variations?.some((v) => v.size) && (
                <div className="space-y-2">
                  <label className="flex items-center gap-1 font-medium text-gray-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Size
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ...new Set(
                        item?.variation?.product?.variations?.map((v) => v.size)
                      ),
                    ].map((size, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={selectedSize === size}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                          selectedSize === size
                            ? "bg-red-600 text-white shadow-md shadow-red-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        onClick={() => handleSizeClick(size)}
                      >
                        {selectedSize === size && (
                          <IoMdCheckmark className="inline mr-1" />
                        )}
                        {size}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Selection */}
              {item?.variation?.product?.variations?.some((v) => v.color) && (
                <div className="space-y-2">
                  <label className="flex items-center gap-1 font-medium text-gray-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ...new Set(
                        item?.variation?.product?.variations?.map(
                          (v) => v.color
                        )
                      ),
                    ].map((color, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={selectedColor === color}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                          selectedColor === color
                            ? "bg-red-600 text-white shadow-md shadow-red-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        onClick={() => handleColorClick(color)}
                      >
                        {selectedColor === color && (
                          <IoMdCheckmark className="inline mr-1" />
                        )}
                        {color}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Brand Selection */}
              {item?.variation?.product?.variations?.some((v) => v.brand) && (
                <div className="space-y-2">
                  <label className="flex items-center gap-1 font-medium text-gray-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Brand
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ...new Set(
                        item?.variation?.product?.variations?.map(
                          (v) => v.brand
                        )
                      ),
                    ].map((brand, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={selectedBrand === brand}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                          selectedBrand === brand
                            ? "bg-red-600 text-white shadow-md shadow-red-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        onClick={() => handleBrandClick(brand)}
                      >
                        {selectedBrand === brand && (
                          <IoMdCheckmark className="inline mr-1" />
                        )}
                        {brand}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Litre Selection */}
              {item?.variation?.product?.variations?.some((v) => v.litre) && (
                <div className="space-y-2">
                  <label className="flex items-center gap-1 font-medium text-gray-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Litre
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ...new Set(
                        item?.variation?.product?.variations?.map(
                          (v) => v.litre
                        )
                      ),
                    ].map((litre, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={selectedLitre === litre}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                          selectedLitre === litre
                            ? "bg-red-600 text-white shadow-md shadow-red-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        onClick={() => handleLitreClick(litre)}
                      >
                        {selectedLitre === litre && (
                          <IoMdCheckmark className="inline mr-1" />
                        )}
                        {litre}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quantity Selection */}
            <div className="mt-4">
              <div className="flex items-center space-x-1">
                <label className="text-sm font-medium text-gray-700">
                  Quantity:
                </label>
                <div className="flex items-center ml-2 overflow-hidden border border-gray-200 rounded-lg bg-gray-50">
                  <motion.button
                    whileHover={{ backgroundColor: "#f3f4f6" }}
                    whileTap={{ scale: 0.95 }}
                    disabled={item?.quantity === 1 || !selectedVariation?.stock}
                    className={`p-2 ${
                      item?.quantity === 1 || !selectedVariation?.stock
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={async () => {
                      if (item?.quantity > 1 && selectedVariation?.stock) {
                        setLoading(true);
                        await dispatch(
                          updateCart({
                            id: item?.id,
                            data: {
                              quantity: item?.quantity - 1,
                            },
                          })
                        );
                        await dispatch(fetchCartItems({ user_id }));
                        setLoading(false);
                      }
                    }}
                  >
                    <IoMdRemove />
                  </motion.button>
                  <motion.span
                    key={item?.quantity}
                    initial={{ scale: 1.2, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-10 font-semibold text-center"
                  >
                    {item?.quantity}
                  </motion.span>
                  <motion.button
                    whileHover={{ backgroundColor: "#f3f4f6" }}
                    whileTap={{ scale: 0.95 }}
                    disabled={!selectedVariation?.stock}
                    className={`p-2 ${
                      !selectedVariation?.stock
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={async () => {
                      if (selectedVariation?.stock) {
                        setLoading(true);
                        await dispatch(
                          updateCart({
                            id: item?.id,
                            data: {
                              quantity: item?.quantity + 1,
                            },
                          })
                        );
                        await dispatch(fetchCartItems({ user_id }));
                        setLoading(false);
                      }
                    }}
                  >
                    <IoMdAdd />
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // For products without variations
          <div className="mt-4 space-y-4">
            {/* Price */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3"
            >
              <p className="text-2xl font-bold text-red-600">
                Rs. {item?.product?.selling_price || 0 || "Price not available"}
              </p>
            </motion.div>

            {/* Quantity Selection */}
            <div className="mt-4">
              <div className="flex items-center space-x-1">
                <label className="text-sm font-medium text-gray-700">
                  Quantity:
                </label>
                <div className="flex items-center ml-2 overflow-hidden border border-gray-200 rounded-lg bg-gray-50">
                  <motion.button
                    whileHover={{ backgroundColor: "#f3f4f6" }}
                    whileTap={{ scale: 0.95 }}
                    disabled={item?.quantity === 1}
                    className={`p-2 ${
                      item?.quantity === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={async () => {
                      if (item?.quantity > 1) {
                        setLoading(true);
                        await dispatch(
                          updateCart({
                            id: item?.id,
                            data: {
                              quantity: item?.quantity - 1,
                            },
                          })
                        );
                        await dispatch(fetchCartItems({ user_id }));
                        setLoading(false);
                      }
                    }}
                  >
                    <IoMdRemove />
                  </motion.button>
                  <motion.span
                    key={item?.quantity}
                    initial={{ scale: 1.2, opacity: 0.5 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-10 font-semibold text-center"
                  >
                    {item?.quantity}
                  </motion.span>
                  <motion.button
                    whileHover={{ backgroundColor: "#f3f4f6" }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 text-gray-700 hover:bg-gray-100"
                    onClick={async () => {
                      setLoading(true);
                      await dispatch(
                        updateCart({
                          id: item?.id,
                          data: {
                            quantity: item?.quantity + 1,
                          },
                        })
                      );
                      await dispatch(fetchCartItems({ user_id }));
                      setLoading(false);
                    }}
                  >
                    <IoMdAdd />
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {openDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={handleCloseDialog}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="w-full max-w-md p-6 mx-4 bg-white shadow-2xl rounded-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <IoMdTrash className="text-xl text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Remove Item</h3>
              </div>

              <p className="mb-6 text-gray-600">
                Are you sure you want to remove this item from your cart?
              </p>

              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-4 py-2 font-medium text-gray-700 bg-gray-100 rounded-lg"
                  onClick={handleCloseDialog}
                >
                  Cancel
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-1 px-4 py-2 font-medium text-white bg-red-500 rounded-lg"
                  onClick={() => handleDelete(selectedItemId)}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-4 h-4 mr-1 border-2 rounded-full border-t-white border-white/30 animate-spin"></div>
                  ) : (
                    <IoMdTrash className="text-lg" />
                  )}
                  Remove
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CartCard;
